/// <reference types="google.maps" />
import * as React from 'react';
import {
  APIProviderContext,
  Map as GoogleMap,
  useMap,
  type MapProps,
} from '@vis.gl/react-google-maps';
import { useKoobMapId } from './KoobMapsProvider';
import { cameraMoves } from './camera';

type LatLng = { lat: number | string; lng: number | string };

type Props = Omit<
  MapProps,
  'mapId' | 'center' | 'zoom' | 'defaultCenter' | 'defaultZoom' | 'className'
> & {
  /** Applied on mount and whenever its value changes; the user can still pan. */
  center?: LatLng | null;
  /** Applied on mount and whenever it changes; the user can still zoom. */
  zoom?: number;
  /** Receives the google.maps.Map instance, for imperative fitBounds etc. */
  onLoad?: (map: google.maps.Map) => void;
  onUnmount?: (map: google.maps.Map) => void;
  /**
   * Frames the pins whenever the set shown changes (new page, new filters).
   * `center`/`zoom` then only apply while there is no pin.
   */
  fitMarkers?: boolean;
  /** Pixels kept clear around the framed pins, e.g. under a side panel. */
  fitPadding?: number | google.maps.Padding;
};

type RegisterPosition = (
  key: string,
  position: google.maps.LatLngLiteral | null,
) => void;

/** Pins report their position here when the map frames them. */
export const FitMarkersContext = React.createContext<RegisterPosition | null>(
  null,
);

// One pin, or pins on one spot: city level rather than Google's deepest zoom.
const SINGLE_SPOT_ZOOM = 13;

// Framed pins keep clear of the edges, where the pin heads and groups clip.
const FIT_PADDING_PX = 56;

/**
 * The open popup, one per map: a single pin (one key), or pins the clusterer
 * cannot split, paged with `index` and shown at the group's `position`.
 */
export type OpenPopup = {
  keys: string[];
  index: number;
  position?: google.maps.LatLngLiteral;
} | null;

export const OpenPopupContext = React.createContext<
  [OpenPopup, (open: OpenPopup) => void]
>([null, () => undefined]);

function toLatLng(center?: LatLng | null) {
  const lat = parseFloat(`${center?.lat}`);
  const lng = parseFloat(`${center?.lng}`);

  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
}

function CameraBridge({
  center,
  zoom,
  onLoad,
  onUnmount,
  hasFramedPins,
}: Pick<Props, 'zoom' | 'onLoad' | 'onUnmount'> & {
  center: google.maps.LatLngLiteral | null;
  hasFramedPins: () => boolean;
}) {
  const map = useMap();
  const callbacks = React.useRef({ onLoad, onUnmount });
  callbacks.current = { onLoad, onUnmount };

  React.useEffect(() => {
    if (!map) {
      return undefined;
    }
    callbacks.current.onLoad?.(map);

    return () => callbacks.current.onUnmount?.(map);
  }, [map]);

  // Mount values are the map's defaults already; applying them again after
  // onLoad would undo the camera onLoad just set. Only later changes move it,
  // compared by value: callers often build the center inline.
  const applied = React.useRef({ lat: center?.lat, lng: center?.lng, zoom });

  React.useEffect(() => {
    if (!map) {
      return;
    }
    const previous = applied.current;
    applied.current = { lat: center?.lat, lng: center?.lng, zoom };
    // Framed pins win over a center that loads after them.
    if (hasFramedPins()) {
      return;
    }
    const moves = cameraMoves(previous, applied.current);
    if (moves.center) {
      map.setCenter(moves.center);
    }
    if (moves.zoom !== null) {
      map.setZoom(moves.zoom);
    }
  }, [map, center?.lat, center?.lng, zoom]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}

function FitBridge({
  positions,
  scheduleRef,
  padding,
}: {
  positions: Map<string, google.maps.LatLngLiteral>;
  scheduleRef: React.MutableRefObject<() => void>;
  padding?: number | google.maps.Padding;
}) {
  const map = useMap();
  const framed = React.useRef('');
  const paddingKey = JSON.stringify(padding ?? 0);

  React.useEffect(() => {
    if (!map) {
      return undefined;
    }
    let timer: ReturnType<typeof setTimeout> | undefined;

    // Pins register one by one as they mount: frame once they all have.
    scheduleRef.current = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        const points = [...positions.values()];
        const signature = points
          .map(({ lat, lng }) => `${lat},${lng}`)
          .sort()
          .join('|');
        if (points.length === 0 || signature === framed.current) {
          return;
        }
        framed.current = signature;

        const bounds = new google.maps.LatLngBounds();
        points.forEach((point) => bounds.extend(point));
        if (bounds.getNorthEast().equals(bounds.getSouthWest())) {
          map.setCenter(points[0]);
          map.setZoom(SINGLE_SPOT_ZOOM);
        } else {
          map.fitBounds(bounds, JSON.parse(paddingKey));
        }
      });
    };
    scheduleRef.current();

    return () => {
      clearTimeout(timer);
      scheduleRef.current = () => undefined;
    };
  }, [map, positions, scheduleRef, paddingKey]);

  return null;
}

/**
 * A Google map with the KOOB cloud style. Fills its parent unless `style`
 * says otherwise.
 */
export function KoobMap({
  center,
  zoom,
  onLoad,
  onUnmount,
  style,
  onClick,
  fitMarkers = false,
  fitPadding = FIT_PADDING_PX,
  children,
  ...options
}: Props) {
  const positions = React.useRef(
    new Map<string, google.maps.LatLngLiteral>(),
  ).current;
  const scheduleFit = React.useRef<() => void>(() => undefined);
  const registerPosition = React.useCallback<RegisterPosition>(
    (key, position) => {
      if (position) {
        positions.set(key, position);
      } else {
        positions.delete(key);
      }
      scheduleFit.current();
    },
    [positions],
  );
  const api = React.useContext(APIProviderContext);
  const mapId = useKoobMapId();
  const initialCenter = toLatLng(center);
  const openPopup = React.useState<OpenPopup>(null);
  const setOpenPopup = openPopup[1];
  const size = { width: '100%', height: '100%', ...style };

  // No provider: the app chose not to load Google (Cypress). <Map> would
  // throw, so keep the layout and skip the map.
  if (!api) {
    return <div style={size} />;
  }

  return (
    <GoogleMap
      mapId={mapId}
      defaultCenter={initialCenter ?? undefined}
      defaultZoom={zoom}
      style={size}
      mapTypeControl={false}
      streetViewControl={false}
      fullscreenControl={false}
      // Google's own POIs open Google popups; KOOB content is the point.
      clickableIcons={false}
      {...options}
      // A click on the map itself, not on a pin or a popup, closes the popup.
      onClick={(event) => {
        setOpenPopup(null);
        onClick?.(event);
      }}
    >
      <CameraBridge
        center={initialCenter}
        zoom={zoom}
        onLoad={onLoad}
        onUnmount={onUnmount}
        hasFramedPins={() => fitMarkers && positions.size > 0}
      />
      {fitMarkers && (
        <FitBridge
          positions={positions}
          scheduleRef={scheduleFit}
          padding={fitPadding}
        />
      )}
      <FitMarkersContext.Provider value={fitMarkers ? registerPosition : null}>
        <OpenPopupContext.Provider value={openPopup}>
          {children}
        </OpenPopupContext.Provider>
      </FitMarkersContext.Provider>
    </GoogleMap>
  );
}
